import { EventType, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import axios from 'axios';

const clientId = process.env.REACT_APP_INV_CLIENT_ID;
const tenantId = process.env.REACT_APP_INV_TENANT_ID;
const authority = `https://login.microsoftonline.com/${tenantId}`;

console.log("[MSAL ENV] clientId:", clientId);
console.log("[MSAL ENV] tenantId/domain:", tenantId);

export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: clientId,
    authority: authority,
    redirectUri: window.location.origin
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false
  },
  system: {
    // Silent renewal happens in a hidden iframe. The 6s default is tight on a
    // cold cache or a slow round trip, and when it lapses MSAL reports
    // monitor_window_timeout - which reads like a failure but is usually just
    // impatience.
    iframeHashTimeout: 12000,
    loadFrameTimeout: 12000
  }
});

/**
 * Silent-renewal failures that are worth trying again.
 *
 * These say "the iframe did not report back in time", not "this user may not
 * have a token". Treating them as fatal strands the app on an error screen for
 * something that usually works on the second attempt.
 */
const TRANSIENT_TOKEN_ERRORS = new Set([
  'monitor_window_timeout',
  'iframe_closed_prematurely',
  'block_iframe_reload'
]);

export function isTransientAuthError(error) {
  return TRANSIENT_TOKEN_ERRORS.has(error?.errorCode);
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const loginRequest = { scopes: ["User.Read"] };

let initPromise = null;

export async function ensureInitialized() {
  if (!initPromise) {
    initPromise = msalInstance.initialize();
  }
  return initPromise;
}

let loginInProgress = false;

export async function loginUser(loginRequest) {
  await ensureInitialized();

  if (loginInProgress) {
    throw new Error('interaction_in_progress');
  }

  loginInProgress = true;

  try {
    const response = msalInstance.loginRedirect ?
      (await msalInstance.loginPopup(loginRequest)) :
      (await msalInstance.loginPopup(loginRequest));
    return response.account;
  } finally {
    loginInProgress = false;
  }
}

export async function logoutUser() {
  await ensureInitialized();
  return msalInstance.logoutRedirect();
}


export async function getGraphProfile(select = "displayName,mail,userPrincipalName,department,officeLocation,jobTitle") {
  const account = msalInstance.getActiveAccount() ||msalInstance.getAllAccounts()[0];
  if (!account) {
    return null;
  }

  let accessToken;
  try {
    accessToken = await acquireTokenWithRetry(account);
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      return null;
    }
    throw e;
  }

  try {

    const res = await axios.get(`https://graph.microsoft.com/v1.0/me?$select=${encodeURIComponent(select)}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }, 
      validateStatus: () => true
    });

    const ct = res.headers['content-type'] || "";
    console.log("[Graph] status:", res.status, res.statusText);
    console.log("[Graph] headers:", res.headers);
    console.log("[Graph] data:", res.data);

    if (res.status < 200 || res.status >= 300 || ct.includes("text/html")) {
      return null;
    }
    return res.data; // axios already parsed JSON
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      return null;
    }
    throw e;
  }
}

/**
 * One silent attempt, then one more if the first merely timed out.
 *
 * Deliberately not a loop: if two attempts cannot renew the token, something is
 * actually wrong and the caller should be told rather than kept waiting.
 */
async function acquireTokenWithRetry(account) {
  try {
    const { accessToken } = await msalInstance.acquireTokenSilent({
      account,
      scopes: ["User.Read"]
    });
    return accessToken;
  } catch (e) {
    if (!isTransientAuthError(e)) throw e;

    console.warn(`[MSAL] silent token renewal timed out (${e.errorCode}); retrying once`);
    await wait(500);

    const { accessToken } = await msalInstance.acquireTokenSilent({
      account,
      scopes: ["User.Read"],
      forceRefresh: true
    });
    return accessToken;
  }
}


export async function getSwaUser() {
  try {
    const res = await fetch('/.auth/me', { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    return data[0]?.clientPrincipal || null;
  } catch (e) {
    console.error("Error fetching SWA user:", e);
    return null;
  }
}


export async function bootstrapMsal() {
  await ensureInitialized();

  const redirectResult = await msalInstance.handleRedirectPromise();
  if(redirectResult?.account) {
    msalInstance.setActiveAccount(redirectResult.account);
  }

  const existing = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  if (existing) {
    msalInstance.setActiveAccount(existing);
  }

  msalInstance.addEventCallback((e) => {
    if(e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && e.payload?.account) {
      msalInstance.setActiveAccount(e.payload.account);
    }
  });

  return msalInstance.getActiveAccount();
}